import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Recipe } from '../types/recipes';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useRecipeCouponsUI } from '../hooks/use-recipe-coupons-ui';
import { RecipeCouponBadge } from './RecipeCouponBadge';
import { RecipeCouponsSheet } from './RecipeCouponsSheet';
import { IconChefHat, IconClock, IconUsersGroup } from '@tabler/icons-react';

interface RecipeCardProps {
  recipe: Recipe;
  onClick?: () => void;
}

export const RecipeCard = ({ recipe, onClick }: RecipeCardProps) => {
  const {
    matchingCoupons,
    hasMatches,
    couponsSheetOpen,
    setCouponsSheetOpen,
    handleBadgeClick,
  } = useRecipeCouponsUI(recipe);
  const imageUrl = recipe.RecipeImage?.url || '';
  const imageAlt = recipe.RecipeImage?.alternativeText || recipe.RecipeTitle;

  return (
    <>
    <Card
      className="hover:shadow-xl transition-all duration-3000 cursor-pointer overflow-hidden group"
      onClick={onClick}
    >
      <div className="relative overflow-hidden">
        <AspectRatio ratio={16 / 18}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={imageAlt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-3000"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <IconChefHat className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
        </AspectRatio>
        {hasMatches && (
          <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
            <RecipeCouponBadge count={matchingCoupons.length} onClick={handleBadgeClick} />
          </div>
        )}
        <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 flex gap-1 sm:gap-2 flex-wrap">
          {recipe.recipe_cuisine && (
            <Badge variant="default" className="text-xs bg-emerald-500/90 backdrop-blur-sm">
              {recipe.recipe_cuisine.RecipeCuisine}
            </Badge>
          )}
          {recipe.recipe_type && (
            <Badge variant="default" className="text-xs bg-indigo-500/90 backdrop-blur-sm">
              {recipe.recipe_type.RecipeType}
            </Badge>
          )}
        </div>
      </div>
      
      <div className="p-3 sm:p-4 pb-2 sm:pb-3">
        <h3 className="text-base sm:text-xl font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
          {recipe.RecipeTitle}
        </h3>
        {recipe.RecipeDescription && (
          <p
            className="text-sm text-muted-foreground mt-1.5 hidden sm:[display:-webkit-box] sm:[-webkit-line-clamp:2] sm:[-webkit-box-orient:vertical] sm:overflow-hidden"
          >
            {recipe.RecipeDescription}
          </p>
        )}
      </div>
      
      <CardContent className="p-3 sm:p-4 pt-0">
        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
          {recipe.RecipeTotalTime && (
            <div className="flex items-center gap-1">
              <IconClock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{recipe.RecipeTotalTime}</span>
            </div>
          )}
          {recipe.RecipeServingSize && (
            <div className="flex items-center gap-1">
              <IconUsersGroup className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>{recipe.RecipeServingSize}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>

    {couponsSheetOpen && (
      <RecipeCouponsSheet
        open={couponsSheetOpen}
        onOpenChange={setCouponsSheetOpen}
        recipe={recipe}
        matchingCoupons={matchingCoupons}
      />
    )}
    </>
  );
};



